import { ROLES, type Role } from '@/shared/auth';
import type { UserStatusFilter } from '@/shared/api';

/**
 * Übersetzung zwischen URL-Suchparametern und Listenzustand der Benutzerverwaltung.
 *
 * <p>Gleiche Bauart wie `bookingsListSearch` aus Block A und aus demselben Grund (§5): Reload,
 * Zurück-Taste und ein geteilter Link sollen dieselbe Auswahl wiederherstellen. Reine Funktionen,
 * damit der Vertrag testbar ist und nicht an der Komponente hängt.</p>
 */
export interface UsersSearch {
  q?: string;
  role?: string;
  status?: string;
  page?: number;
}

export interface UsersFilterState {
  query: string;
  role: Role | '';
  status: UserStatusFilter | '';
}

export const EMPTY_USER_FILTERS: UsersFilterState = { query: '', role: '', status: '' };

/** Rollen, die in dieser Liste überhaupt vorkommen können — Reihenfolge = Anzeigereihenfolge. */
export const FILTERABLE_ROLES: Role[] = [
  ROLES.WorkspaceAdmin,
  ROLES.Staff,
  ROLES.Housekeeping,
  ROLES.Owner,
  ROLES.Guest,
];

const STATUSES: UserStatusFilter[] = ['Active', 'Invited', 'Inactive'];

export function usersFiltersFromSearch(search: UsersSearch): UsersFilterState {
  return {
    query: search.q ?? '',
    role: FILTERABLE_ROLES.find((r) => r === search.role) ?? '',
    status: STATUSES.find((s) => s === search.status) ?? '',
  };
}

export function usersPageFromSearch(search: UsersSearch): number {
  const page = Number(search.page);
  return Number.isFinite(page) && page > 1 ? Math.floor(page) : 1;
}

/** Defaults werden weggelassen, damit eine ungefilterte Liste eine saubere URL behält. */
export function usersSearchFromState(state: {
  filters: UsersFilterState;
  page: number;
}): UsersSearch {
  return {
    q: state.filters.query.trim() || undefined,
    role: state.filters.role || undefined,
    status: state.filters.status || undefined,
    page: state.page > 1 ? state.page : undefined,
  };
}

export function isUsersFiltered(s: UsersFilterState): boolean {
  return s.query.trim() !== '' || s.role !== '' || s.status !== '';
}
